import React, {useCallback} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "~/stores";
import type {AdvancedFilter, HttpQueryParams, PageResponse} from "~/services/ApiTypes";
import type {Option, SelectionResult} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {ApiDropdownFilterComponent, DEFAULT} from "~/components/visualization/api-dropdown-filter/ApiDropdownFilterComponent";

/**
 * Loads the options of a filter for a search term, given the other filters.
 * Returns null when the options cannot be loaded yet.
 */
export type OptionSource = (databagId: number, params: HttpQueryParams, search: string) => Promise<Option[]> | null;

type Props = {
    label: string
    /** Current selection, from the filters being edited */
    selection: SelectionResult
    /** Filter keys left out when searching the options (the filter's own keys) */
    exclude: string[]
    source: OptionSource
    onChange: (selection: SelectionResult) => AdvancedFilter
    showOptionSubLabel?: boolean
    inclusiveOnly?: boolean
}

/**
 * A filter of the Spend Landscape filter bar that searches its options in the API.
 * Changes are staged and applied with the Apply button.
 */
export const InsightOptionFilter: React.FC<Props> = observer(({
                                                                  label,
                                                                  selection,
                                                                  exclude,
                                                                  source,
                                                                  onChange,
                                                                  showOptionSubLabel,
                                                                  inclusiveOnly,
                                                              }) => {
    const {bagStore} = useStores();
    const manager = bagStore.advanced;
    // The dropdown resets its pending choice when the selection object changes, so keep it stable
    const selectionKey = JSON.stringify(selection ?? DEFAULT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableSelection = React.useMemo(() => selection ?? DEFAULT, [selectionKey]);
    const databagId = manager.databagId;
    const ready = manager.ready;
    const params = manager.filterStore.editingQueryParams(...exclude);
    const paramsKey = JSON.stringify(params);

    const getOptions = useCallback(
        (search: string): Promise<PageResponse<Option>> | null => {
            if (!databagId || !ready) return null;
            const promise = source(databagId, JSON.parse(paramsKey), search);
            return promise?.then(results => ({count: results.length, next: '', previous: '', results})) ?? null;
        },
        [databagId, ready, paramsKey, source]
    );

    if (!ready) return null;
    return <ApiDropdownFilterComponent
        selection={stableSelection}
        label={label}
        labelMode="chip"
        getOptions={getOptions}
        showOptionSubLabel={showOptionSubLabel}
        inclusiveOnly={inclusiveOnly}
        onSelectionChange={s => manager.filterStore.stage(onChange(s))}
    />
});
